import type { ReactNode, FC } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from './cn.js';

export interface TablePaginationProps {
  /** Текущая страница, нумерация с единицы. */
  page: number;
  /** Всего страниц; вызывающий отдаёт значение не меньше единицы. */
  totalPages: number;
  /** Получает новый номер страницы, уже подрезанный до [1, totalPages]. */
  onPageChange: (page: number) => void;
  /** Готовая строка вида «Показаны 1-20 из 34»; форматирование - забота вызывающего. */
  summary?: ReactNode;
  className?: string;
}

const NAV_BUTTON =
  'grid size-7 cursor-pointer place-items-center rounded-md border border-ink-200 bg-white ' +
  'text-ink-600 hover:enabled:bg-ink-100 disabled:cursor-default disabled:opacity-40';

/** Пагинация под таблицей: сводка слева, переход по страницам справа. */
export const TablePagination: FC<TablePaginationProps> = ({
  page,
  totalPages,
  onPageChange,
  summary,
  className,
}) => {
  const go = (next: number) =>
    onPageChange(Math.min(totalPages, Math.max(1, next)));

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <span>{summary}</span>
      <div className="ml-auto flex items-center gap-1.5">
        <button
          type="button"
          aria-label="Предыдущая страница"
          title="Предыдущая страница"
          disabled={page <= 1}
          onClick={() => go(page - 1)}
          className={NAV_BUTTON}
        >
          <ChevronLeft aria-hidden className="size-3.5" />
        </button>
        <span className="tabular-nums">
          {page} / {totalPages}
        </span>
        <button
          type="button"
          aria-label="Следующая страница"
          title="Следующая страница"
          disabled={page >= totalPages}
          onClick={() => go(page + 1)}
          className={NAV_BUTTON}
        >
          <ChevronRight aria-hidden className="size-3.5" />
        </button>
      </div>
    </div>
  );
};
